import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CakeTopologyBidirectionalDirection } from 'src/app/shared/models/ef-cake-topology.model';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { EfCakeTopologyStateUtils } from '../../utils/ef-cake-topology-state.utils';

type EdgeStyle = 'solid' | 'dashed';

@Component({
  selector: 'app-ef-cake-topology-structure',
  templateUrl: './ef-cake-topology-structure.component.html',
  styleUrls: ['./ef-cake-topology-structure.component.css']
})
export class EFCakeTopologyStructureComponent {
  @Input() readonlyMode: boolean = false;
  @Input() sizeVariant: 'small' | 'mid' | 'large' = 'mid';
  @Input() groupColor: string = EFateConstants.GROUP_COLORS[0];
  @Input() nodeNames: Record<string, string> = { ...EFateConstants.DEFAULT_NODE_NAMES };
  @Input() activeEdges: Set<string> = new Set<string>();
  @Input() activeStatusNodes: Set<string> = new Set<string>(EFateConstants.DEFAULT_ACTIVE_STATUS_NODES);
  @Input() bidirectionalEdgeDirections: Record<string, CakeTopologyBidirectionalDirection> = {
    ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS
  };

  @Output() singleDirectionEdgeToggle = new EventEmitter<string>();
  @Output() bidirectionalEdgeToggle = new EventEmitter<string>();
  @Output() statusNodeToggle = new EventEmitter<string>();

  onSingleDirectionEdgeClick(edgeId: string): void {
    if (this.readonlyMode) {
      return;
    }

    if (!this.isEdgeEnabled(edgeId) && !this.isEdgeActive(edgeId)) {
      return;
    }

    this.singleDirectionEdgeToggle.emit(edgeId);
  }

  onBidirectionalEdgeClick(edgeId: string): void {
    if (this.readonlyMode) {
      return;
    }

    if (!this.isBidirectionalEdgeEnabled(edgeId) && !this.isBidirectionalEdgeActive(edgeId)) {
      return;
    }

    this.bidirectionalEdgeToggle.emit(edgeId);
  }

  onStatusNodeClick(statusNodeId: string): void {
    if (this.readonlyMode) {
      return;
    }

    if (!this.isStatusNodeEnabled(statusNodeId)) {
      return;
    }

    this.statusNodeToggle.emit(statusNodeId);
  }

  isEdgeActive(edgeId: string): boolean {
    return this.activeEdges.has(edgeId);
  }

  isBidirectionalEdgeActive(edgeId: string): boolean {
    return (this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) !== EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
  }

  isEdgeEnabled(edgeId: string): boolean {
    const sourceNode = EFateConstants.EDGE_TO_SOURCE_NODE[edgeId];
    if (!sourceNode || sourceNode === EFateConstants.DEFAULT_NODE_NAMES.Parent) {
      return true;
    }

    return this.isNodeActivated(sourceNode);
  }

  isBidirectionalEdgeEnabled(edgeId: string): boolean {
    return this.getAvailableBidirectionalDirections(edgeId).length > 0;
  }

  getEdgeMarker(edgeId: string, style: EdgeStyle): string {
    if (!this.isEdgeActive(edgeId)) {
      return style === 'solid' ? 'url(#arrow-solid)' : 'url(#arrow-dashed)';
    }

    return style === 'solid' ? 'url(#arrow-solid-active)' : 'url(#arrow-dashed-active)';
  }

  getBidirectionalMarkerStart(edgeId: string): string | null {
    const direction = this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) {
      return 'url(#arrow-dashed)';
    }

    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.REVERSE) {
      return 'url(#arrow-dashed-active)';
    }

    return null;
  }

  getBidirectionalMarkerEnd(edgeId: string): string | null {
    const direction = this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) {
      return 'url(#arrow-dashed)';
    }

    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD) {
      return 'url(#arrow-dashed-active)';
    }

    return null;
  }

  getNodeClass(nodeId: string): string {
    return this.isNodeActivated(nodeId) ? 'node-active' : 'node-default';
  }

  getNodeDisplayName(nodeId: string): string {
    const value = this.nodeNames[nodeId] || nodeId;
    const maxVisibleLength = EFateConstants.NODE_LABEL_MAX_VISIBLE_LENGTH;

    if (value.length <= maxVisibleLength) {
      return value;
    }

    return `${value.slice(0, maxVisibleLength - 1)}...`;
  }

  isStatusNodeEnabled(statusNodeId: string): boolean {
    const ownerNodeId = EFateConstants.STATUS_NODE_TO_OWNER_NODE[statusNodeId];
    if (!ownerNodeId) {
      return false;
    }

    return this.isNodeActivated(ownerNodeId);
  }

  getStatusNodeClass(statusNodeId: string): string {
    return this.activeStatusNodes.has(statusNodeId) ? 'status-active' : 'status-disabled';
  }

  private isNodeActivated(nodeId: string): boolean {
    return EfCakeTopologyStateUtils.isNodeActivated(nodeId, this.activeEdges, this.bidirectionalEdgeDirections);
  }

  private getAvailableBidirectionalDirections(edgeId: string): CakeTopologyBidirectionalDirection[] {
    const nodes = EFateConstants.BIDIRECTIONAL_EDGE_NODES[edgeId];
    if (!nodes) {
      return [];
    }

    const directions: CakeTopologyBidirectionalDirection[] = [];
    if (this.isNodeActivated(nodes.from)) {
      directions.push(EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD);
    }

    if (this.isNodeActivated(nodes.to)) {
      directions.push(EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.REVERSE);
    }

    return directions;
  }
}